import { BaseDialogProps, ExtractDialogProps, DialogFactory } from 'features/dialogs/types'
import { openDialog } from './openDialog'

export interface QuestionDialogProps<ResponseT> extends BaseDialogProps {
  onResponse: (answer: ResponseT) => void
}

/**
 * Opens a dialog that results with answer.
 *
 * Sends request to open `component` with `props`` to `RootDialogs`
 *
 * `component` must be React component, with implementing `InteractiveComponentProps`,
 * and call `onResponse` & `onClose` when user answered question.
 *
 * Resolves with `onResponse` argument when component calls callback.
 * Resolves with `undefined` if for any reason we were unmounted when question is open.
 */
export async function askQuestionWithDialog<
  PropsT extends QuestionDialogProps<ResponseT>,
  ResponseT = ExtractResponseT<PropsT>
>(factory: () => DialogFactory<PropsT>, props: ExtractDialogProps<Omit<PropsT, 'onResponse'>>): Promise<ResponseT> {
  return new Promise<ResponseT>((resolve) => {
    const dialogProps = {
      ...props,
      onResponse: (answer: ResponseT) => resolve(answer)
    } as ExtractDialogProps<PropsT>
    openDialog({
      factory,
      dialogProps
    })
  })
}

type ExtractResponseT<PropsT> = PropsT extends { onResponse: (answer: infer ResponseT) => void } ? ResponseT : never
